/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useNavigate } from 'react-router'
import { RcmTable } from '~/components/rcm-table/RcmTable'
import { TableHeadRow, TableRow, TableHeadCell } from '~/components/table/TableComponents'
import TableDateTimeCell from '~/components/shared/TableDateTimeCell'
import { useTranslation } from 'react-i18next'
import { useModal } from '~/contexts/ModalContext'
import WorkflowDetailsModal from '~/components/modals/WorkflowDetailsModal'
import { getDetails, getFileContentUrl } from '~/services/api/files'
import { useServiceContext } from '~/root'
import TableMenu from './TableMenu'
// import TaskDetailsModal from '~/screens/tasks/Actions/details'

interface ActivityLog {
  id?: number
  createdAt?: string | number
  createdById?: number
  createdByName?: string
  action?: string
  itemText?: string
  itemId?: number
  type?: string
  detail?: string | object
  esignatureType?: string
  esignatureComment?: string
}

const WF_MATCH_REGEX = /^WF(\d{5,})/

const FsiActivityTable: React.FC = () => {
  const { t: translate } = useTranslation()
  const { showModal } = useModal()
  const navigate = useNavigate()
  const context = useServiceContext()

  const downloadFile = async (fileId: number) => {
    try {
      // Show download preparing toast
      context.service.toast.info(translate('BROWSER_DOWNLOAD_IS_PREPARING'))

      // Get the download URL
      const data = await getFileContentUrl(fileId)

      if (data.message) {
        context.service.toast.info(data.message)
      }

      if (data.url) {
        // Trigger download by creating a temporary anchor and clicking it
        const link = document.createElement('a')
        link.href = data.url
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('Error downloading file:', error)
      context.service.toast.error(translate('ERROR_DOWNLOADING_FILE'))
    }
  }

  const hasReferenceItem = (log: ActivityLog) => {
    if (!log.itemText || !log.itemId) {
      return false
    }
    if (log.type === 'WORKFLOW') {
      return true
    } else if (log.type === 'DOCUMENT' && log.action !== 'Document Deleted') {
      return true
    } else if (log.type === 'ACL') {
      return true
    } else if (log.type === 'TASK') {
      return WF_MATCH_REGEX.test(log.itemText)
    } else if (log.action === 'Documents Migrated') {
      return true
    }
    return false
  }

  const openReferenceItem = async (log: ActivityLog) => {
    if (!log.itemId) return

    switch (log.type) {
      case 'WORKFLOW':
      case 'TASK':
        // Open workflow details modal
        showModal(<WorkflowDetailsModal wfId={log.itemId} />)
        break

      // TODO: Re-enable task details modal if required
      // case 'TASK':
      //   // Open task details modal
      //   showModal(<TaskDetailsModal taskId={log.itemId} onClose={() => {}} />)
      //   break

      case 'DOCUMENT':
      case 'ACL':
      case 'FOLDER':
        if (log.action === 'Documents Migrated') {
          // Download original file for migrated documents
          await downloadFile(log.itemId)
          return
        }

        // Check if it's a CSV or JSON file that should be downloaded
        const fileExtension = log.itemText?.split('.').pop()?.toLowerCase()
        if (fileExtension && ['csv', 'json'].includes(fileExtension)) {
          // Download CSV or JSON files directly
          await downloadFile(log.itemId)
          return
        }

        // Get file details to determine if it's a folder or file
        try {
          const data = await getDetails(log.itemId)
          if (data.type === 'FOLDER') {
            // Navigate to browser folder view
            navigate(`/browser/${log.itemId}`)
          } else {
            // Navigate to file detail view
            navigate(`/show-file/${log.itemId}`)
          }
        } catch (error) {
          console.error('Error fetching file details:', error)
        }
        break
    }
  }

  return (
    <RcmTable
      className="chita-table"
      noResultsText={translate('NO_RESULTS_FILTERED')}
      disableOnInitLoad={true}
      simplePaginate={true}
      HeadRow={
        <TableHeadRow>
          <TableHeadCell sort={'createdAt'}>{translate('REPORTS_TABLE_TH_DATETIME')}</TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }}>{translate('REPORTS_TABLE_TH_USER')}</TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }}>{translate('REPORTS_TABLE_TH_ACTION')}</TableHeadCell>
          <TableHeadCell style={{ fontSize: '12px' }}>{translate('REPORTS_TABLE_TH_ITEM')}</TableHeadCell>
          <TableHeadCell className="actions" style={{ fontSize: '12px' }}>
            {translate('TASKS_TABLE_HEADER_ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={(props: { row: ActivityLog }) => {
        const log = props.row
        const userName = log.createdByName || translate('NOT_AVAILABLE')

        return (
          <TableRow {...props}>
            <td style={{ width: 220 }}>
              <TableDateTimeCell val={log.createdAt} includeTime={true} />
            </td>
            <td>{userName}</td>
            <td>{log.action}</td>
            <td>
              {hasReferenceItem(log) ? (
                <div
                  style={{
                    height: 15,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 700
                  }}
                >
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      openReferenceItem(log)
                    }}
                    title={log.itemText}
                  >
                    {log.itemText}
                  </a>
                </div>
              ) : (
                <div
                  style={{
                    height: 15,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 700
                  }}
                >
                  <span title={log.itemText}>{log.itemText || translate('NOT_AVAILABLE')}</span>
                </div>
              )}
            </td>
            <td className="actions">
              <TableMenu log={log} />
            </td>
          </TableRow>
        )
      }}
    />
  )
}

export default FsiActivityTable
